import { SharedFileRecord } from '../types';
import { apiGet, apiDelete } from './api';
import { uploadFileToWorker, isVideoFile, deleteFileFromWorker, getWorkerFileUrl } from './workerFileStorage';

function getActiveUid(): string {
  return 'user';
}

export function formatBytes(bytes: number, decimals = 2): string {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export function detectFileType(fileName: string, mimeType?: string): SharedFileRecord['type'] {
  const name = fileName.toLowerCase();
  if (mimeType?.startsWith('video/') || name.endsWith('.mp4') || name.endsWith('.mov') || name.endsWith('.webm') || name.endsWith('.mkv') || name.endsWith('.avi')) {
    return 'video';
  }
  if (name.endsWith('.pdf') || mimeType === 'application/pdf') {
    return 'pdf';
  }
  if (name.endsWith('.doc') || name.endsWith('.docx') || mimeType?.includes('word')) {
    return 'word';
  }
  if (name.endsWith('.ppt') || name.endsWith('.pptx') || mimeType?.includes('powerpoint') || mimeType?.includes('presentation')) {
    return 'ppt';
  }
  if (mimeType?.startsWith('image/') || name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.gif') || name.endsWith('.webp')) {
    return 'image';
  }
  return 'other';
}

let cachedSharedFiles: SharedFileRecord[] = [];

function getRegisteredIds(): Set<string> {
  if (typeof window !== 'undefined') {
    const g = window as any;
    g.__registeredFileIds = g.__registeredFileIds || new Set<string>();
    return g.__registeredFileIds;
  }
  return new Set<string>();
}

/**
 * Registers an uploaded file ID to avoid duplicate creations.
 */
export function markFileIdAsRegistered(id: string): void {
  getRegisteredIds().add(id);
}

/**
 * Saves a file or link record to the backend.
 * If already uploaded via uploadFileToWorker, it is a no-op.
 * If it is a shared link, we create a tiny dummy metadata file.
 */
export async function saveFileRecordToCloud(record: SharedFileRecord): Promise<void> {
  const recordId = record.id || record.fileId;
  if (recordId && getRegisteredIds().has(recordId)) {
    // Already uploaded and saved on server
    return;
  }

  // If it's a web link, register it on the server as a dummy file payload
  const isLink = record.fileUrl && (record.fileUrl.startsWith('http://') || record.fileUrl.startsWith('https://')) && !record.fileUrl.includes('/api/files/');
  
  if (isLink) {
    try {
      // Create a dummy file representing the link metadata
      const dummyContent = `LINK:${record.fileUrl}`;
      const dummyBlob = new Blob([dummyContent], { type: 'text/plain' });
      
      const packedName = `link__${record.section || 'general'}__${encodeURIComponent(record.moduleTitle || 'اشتراک لینک')}__${encodeURIComponent(record.fileUrl)}__${record.name}`;

      const formData = new FormData();
      formData.append('file', dummyBlob, packedName);

      const response = await fetch('/api/files/upload', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (response.ok) {
        const resJson = await response.json();
        if (resJson && resJson.success && resJson.data) {
          getRegisteredIds().add(resJson.data.id);
        }
      }
    } catch (err) {
      console.warn('Failed saving shared link metadata to D1:', err);
    }
  }
}

/**
 * Syncs all shared file records from /api/files
 */
export async function fetchCloudFileRecords(): Promise<SharedFileRecord[]> {
  try {
    const res = await apiGet<any[]>('/api/files');
    if (res.success && Array.isArray(res.data)) {
      cachedSharedFiles = res.data.map(item => {
        const rawName = item.file_name || item.fileName || item.name || '';
        
        let section: SharedFileRecord['section'] = 'general';
        let moduleTitle = 'پیوست عمومی';
        let fileUrl = item.fileUrl || item.publicUrl || `/api/files/${item.id}/download`;
        let displayName = rawName;

        if (rawName.startsWith('link__')) {
          const parts = rawName.split('__');
          if (parts.length >= 5) {
            section = (parts[1] as any) || 'general';
            moduleTitle = decodeURIComponent(parts[2]);
            fileUrl = decodeURIComponent(parts[3]);
            displayName = parts.slice(4).join('__');
          }
        } else if (rawName.startsWith('file__')) {
          const parts = rawName.split('__');
          if (parts.length >= 4) {
            section = (parts[1] as any) || 'general';
            moduleTitle = decodeURIComponent(parts[2]);
            displayName = parts.slice(3).join('__');
          }
        }

        // Keep ID registered
        getRegisteredIds().add(item.id);

        return {
          id: item.id,
          fileId: item.id,
          key: item.key || item.r2_key,
          name: displayName,
          type: detectFileType(displayName, item.mimeType || item.mime_type),
          section,
          moduleTitle,
          fileUrl,
          size: item.size || item.file_size || 0,
          fileSize: formatBytes(item.size || item.file_size || 0),
          uploadDate: item.createdAt || item.created_at || new Date().toISOString(),
          userId: item.userId || item.user_id,
          uploadedBy: item.uploadedBy || 'کاربر',
          folderPath: `/storage/${section}/`
        };
      });
    }
  } catch (err) {
    console.warn('Error fetching file records from local server:', err);
  }
  return [...cachedSharedFiles];
}

/**
 * Deletes a file record from local API
 */
export async function deleteCloudFileRecord(fileId: string): Promise<void> {
  cachedSharedFiles = cachedSharedFiles.filter(f => f.id !== fileId && f.fileId !== fileId);
  getRegisteredIds().delete(fileId);

  await deleteFileFromWorker(fileId).catch(err => {
    console.warn('Error deleting shared file from local server:', err);
  });
}

/**
 * Deletes ALL file records from local API
 */
export async function clearAllCloudFiles(): Promise<void> {
  const records = [...cachedSharedFiles];
  cachedSharedFiles = [];

  for (const rec of records) {
    getRegisteredIds().delete(rec.id);
    await deleteFileFromWorker(rec.id).catch(() => {});
  }
}

export function subscribeToCloudFiles(onUpdate: (files: SharedFileRecord[]) => void) {
  fetchCloudFileRecords().then(files => onUpdate(files));
  return () => {};
}
